import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ListBase, Pagination, Title, useGetList, useGetOne, useListContext } from 'react-admin';
import { Alert, Box, Button, IconButton, Skeleton, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import LoyaltyIcon from '@mui/icons-material/Loyalty';
import FeatureIntro from '../../components/FeatureIntro';
import Permission from '../../components/Permission';
import { PERMISSIONS } from '../../constants';
import { __t } from '../../i18n/t';
import { getChainId } from '../../lib';
import ManualLinkButton from '../../manual/components/ManualLinkButton';
import { useDuplicateDiscount } from './CloneDiscountButton';
import { discountListQueryOptions, hasDiscountChain } from './discountListChain';
import DiscountCard from './list/DiscountCard';
import DisableDiscountDialog from './list/DisableDiscountDialog';
import DuplicateDatesDialog from './list/DuplicateDatesDialog';
import { DiscountStatusKey, DiscountTypeKey, fmtN, typeLabel } from './list/discountStatus';
import { NamedItem, cardTitle } from './list/discountSummary';
import { FilterSelect, SearchField, StatusTabs, TabItem, TypeChips } from './list/ListControls';
import { routeSearch } from './list/searchRouting';
import { useDiscountLookups } from './list/useDiscountLookups';
import { useDiscountUsage } from './list/useDiscountUsage';

const INK = '#1e293b';
const MUTED = '#475569';
const ALL = 'all';
const PER_PAGE = 10;
const SORT = { field: 'createdAt', order: 'DESC' } as const;
const COUNT_PAGE = { page: 1, perPage: 1 };
// A aba "Ativos" abre primeiro: é o que o dono quer ver ao entrar.
const DEFAULT_FILTER = { status: DiscountStatusKey.active };
const TOAST_MS = 8000;
const FUELS_STALE_MS = 5 * 60 * 1000;

// Estilos em constantes de módulo, não em `sx={{ ... }}` dentro de `cond && (...)`: o plugin de i18n
// embrulha em __t() todo literal de texto que acha num `&&`/ternário de JSX, inclusive valores de `sx`.
const pageSx = {
    maxWidth: 1240, mx: 'auto', width: '100%', boxSizing: 'border-box', p: { xs: 2, md: 3 }, pb: 12,
    display: 'flex', flexDirection: 'column', gap: '18px', color: INK, fontFamily: theme => theme.typography.fontFamily,
};
const headerSx = { display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between', gap: 1.5 };
const titleBoxSx = { display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 };
// O botão do manual é um componente compartilhado de 31 px: aqui ele ganha a altura mínima de toque e um violeta
// escuro (o padrão dele, #7869df sobre branco, dá só 4,3:1 de contraste).
const manualSx = { '& .MuiButton-root': { minHeight: 40, color: '#4433B4', borderColor: '#4433B4', '&:hover': { borderColor: '#4433B4', bgcolor: 'rgba(68,51,180,.06)' } } };
const headerActionsSx = { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 };
const filtersRowSx = { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px' };
const countSx = { fontSize: 13, color: MUTED, minHeight: 20 };
const paginationSx = { display: 'flex', justifyContent: { xs: 'center', md: 'flex-end' }, overflow: 'hidden' };
const emptyBoxSx = { p: '36px 20px', border: '1.5px dashed #94a3b8', borderRadius: '16px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', textAlign: 'center' };
const toastHostSx = { position: 'fixed', left: 0, right: 0, bottom: 24, zIndex: 1500, display: 'flex', justifyContent: 'center', px: 2, pointerEvents: 'none' };
const toastSx = {
    pointerEvents: 'auto', maxWidth: '100%', display: 'flex', alignItems: 'center', gap: 1.5, pl: 2, pr: 0.5, py: 0.5, minHeight: 48,
    borderRadius: '12px', bgcolor: INK, color: '#fff', fontSize: 14.5, boxShadow: '0 12px 32px rgba(15,23,42,.30)',
};
const toastCloseSx = { width: 40, height: 40, flex: 'none', color: '#e2e8f0', '&:focus-visible': { outline: '2px solid #fff', outlineOffset: -2 } };
const retrySx = { textTransform: 'none', whiteSpace: 'nowrap', minHeight: 40 };
const anywhereSx = { overflowWrap: 'anywhere' };
const cardListSx = { listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 1.5, transition: 'opacity .15s ease' };

// Sem rede a consulta nem sai (queryOptions.enabled); no lugar da lista vazia, o aviso.
const WaitingChain = ({ children }) => {
    const { filterValues } = useListContext();
    if (hasDiscountChain(filterValues, getChainId())) return children;
    return (
        <Box sx={{ p: { xs: 2, md: 3 } }}>
            <Typography>Selecione uma rede para continuar</Typography>
        </Box>
    );
};

// Quantos descontos há em cada situação: um getList de 1 item por aba, só para ler o `total`.
// `settled` = a contagem acabou de vir do servidor (não é a que ficou guardada da última visita).
const useStatusCount = (chainId: string | undefined, status?: DiscountStatusKey): { count: number | undefined; settled: boolean } => {
    const { total, isPending, isFetching, error } = useGetList(
        'discounts',
        { pagination: COUNT_PAGE, sort: SORT, filter: status ? { chainId, status } : { chainId } },
        { enabled: Boolean(chainId), retry: false },
    );
    return { count: isPending || error ? undefined : total, settled: !isPending && !isFetching && !error };
};

const ListToast = ({ message, onClose }: { message: string; onClose: () => void }) => (
    <Box role="status" aria-live="polite" sx={toastHostSx}>
        {message ? (
            <Box sx={toastSx}>
                <CheckCircleIcon aria-hidden="true" sx={{ color: '#79CF62', flexShrink: 0 }} />
                <Box component="span" sx={anywhereSx}>{message}</Box>
                <IconButton aria-label={__t('Fechar aviso')} onClick={onClose} sx={toastCloseSx}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>
        ) : null}
    </Box>
);

const ButtonLink = ({ to, children }) => (
    <Button
        component={Link}
        to={to}
        variant="contained"
        disableElevation
        startIcon={<AddIcon />}
        sx={{
            minHeight: 44, px: 2.5, textTransform: 'none', fontSize: 15, fontWeight: 700, borderRadius: '8px', bgcolor: '#79CF62', color: INK, whiteSpace: 'nowrap',
            '&:hover': { bgcolor: '#6cc456', boxShadow: '0 6px 16px rgba(39,80,48,.18)' },
            '&.Mui-focusVisible': { outline: '2px solid #4433B4', outlineOffset: 2 },
        }}
    >
        {children}
    </Button>
);

const EmptyChain = () => (
    <Box
        component="section"
        aria-labelledby="discounts-empty-title"
        sx={{
            bgcolor: '#fff', border: '1px solid rgba(120,105,223,.14)', borderRadius: '16px', boxShadow: '0 4px 24px rgba(0,0,0,.06)',
            p: { xs: '28px 18px', sm: '40px 32px' }, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '14px', textAlign: 'center',
        }}
    >
        <Box component="span" sx={{ width: 64, height: 64, borderRadius: '18px', bgcolor: 'rgba(120,105,223,.10)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <LoyaltyIcon aria-hidden="true" sx={{ fontSize: 34, color: '#4433B4' }} />
        </Box>
        <Box component="h2" id="discounts-empty-title" sx={{ m: 0, fontSize: 20, fontWeight: 700, color: INK }}>Sua rede ainda não tem descontos</Box>
        <Box component="span" sx={{ fontSize: 15, color: MUTED, lineHeight: 1.5, maxWidth: 520 }}>
            Comece pelo preço do app nos seus postos: um desconto por litro para todo cliente que abastecer pelo aplicativo.
        </Box>
        <ButtonLink to="/discounts/create">Criar o primeiro desconto</ButtonLink>
    </Box>
);

const emptyTitleFor = (status: string): string => {
    switch (status) {
        case DiscountStatusKey.active: return __t('Nenhum desconto ativo');
        case DiscountStatusKey.scheduled: return __t('Nenhum desconto agendado');
        case DiscountStatusKey.ended: return __t('Nenhum desconto encerrado');
        case DiscountStatusKey.disabled: return __t('Nenhum desconto desativado');
        default: return __t('Nenhum desconto');
    }
};

const SkeletonCards = () => (
    <Box aria-hidden="true" sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {[0, 1, 2].map(i => (
            <Skeleton key={i} variant="rounded" animation="wave" height={150} sx={{ borderRadius: '16px' }} />
        ))}
    </Box>
);

const restoreFocus = (element: HTMLElement | null | undefined) => {
    if (element && document.contains(element)) element.focus();
};

const DiscountListPage = () => {
    const { filterValues, setFilters, displayedFilters, data, total, isPending, isFetching, error, refetch } = useListContext();
    const chainId: string | undefined = getChainId() || filterValues.chainId;

    // O que o clique de agora muda parte dos filtros de agora, mesmo se um atraso (a busca) ainda
    // estiver pendente com os de antes.
    const latestFilters = useRef(filterValues);
    latestFilters.current = filterValues;
    const updateFilters = useCallback((patch: Record<string, unknown>) => {
        const next: Record<string, unknown> = { ...latestFilters.current, ...patch };
        Object.keys(next).forEach(key => { if (next[key] === undefined || next[key] === '') delete next[key]; });
        setFilters(next, displayedFilters);
    }, [setFilters, displayedFilters]);

    const status: string = filterValues.status || ALL;
    const type: string = filterValues.type || ALL;
    const stationId: string = filterValues.placeIds?.[0] || '';
    const fuelId: string = filterValues.fuelIds?.[0] || '';
    const search: string = filterValues.search || '';

    // ─── Contadores das abas ────────────────────────────────────────────────────
    const activeCount = useStatusCount(chainId, DiscountStatusKey.active);
    const scheduledCount = useStatusCount(chainId, DiscountStatusKey.scheduled);
    const endedCount = useStatusCount(chainId, DiscountStatusKey.ended);
    const disabledCount = useStatusCount(chainId, DiscountStatusKey.disabled);
    const allCount = useStatusCount(chainId);
    const tabs: TabItem[] = [
        { key: DiscountStatusKey.active, label: __t('Ativos'), count: activeCount.count },
        { key: DiscountStatusKey.scheduled, label: __t('Agendados'), count: scheduledCount.count },
        { key: DiscountStatusKey.ended, label: __t('Encerrados'), count: endedCount.count },
        { key: DiscountStatusKey.disabled, label: __t('Desativados'), count: disabledCount.count },
        { key: ALL, label: __t('Todos'), count: allCount.count },
    ];
    // "Sua rede ainda não tem descontos" só com a contagem fresca: quem acabou de criar o primeiro
    // desconto não pode ver esse aviso por um instante, com o zero da visita anterior.
    const chainIsEmpty = allCount.count === 0 && allCount.settled;

    // ─── Busca (com atraso, para não consultar a cada letra) ────────────────────
    const [typed, setTyped] = useState(search);
    const appliedSearch = useRef(search);
    useEffect(() => {
        // Voltou de outra tela, ou o endereço mudou: a caixa acompanha o filtro.
        if (search !== appliedSearch.current) { appliedSearch.current = search; setTyped(search); }
    }, [search]);

    // ─── Tipo: só mostra Recompensa e Recuperação se a rede usa ─────────────────
    const { data: chain } = useGetOne('chains', { id: chainId as string }, { enabled: Boolean(chainId) });
    const typeItems = [
        { key: ALL, label: __t('Todos os tipos') },
        { key: DiscountTypeKey.default, label: typeLabel(DiscountTypeKey.default) },
        { key: DiscountTypeKey.special, label: typeLabel(DiscountTypeKey.special) },
        ...(chain?.useGift !== false || type === DiscountTypeKey.reward ? [{ key: DiscountTypeKey.reward, label: typeLabel(DiscountTypeKey.reward) }] : []),
        ...(chain?.useCustomerRecovery || type === DiscountTypeKey.recovery ? [{ key: DiscountTypeKey.recovery, label: typeLabel(DiscountTypeKey.recovery) }] : []),
    ];

    // ─── Dados dos cartões ──────────────────────────────────────────────────────
    const records = useMemo(() => data || [], [data]);
    const lookups = useDiscountLookups(records, chainId);
    // Combustíveis vendidos nos postos da rede (o filtro não oferece o que a rede não vende).
    const { data: fuelOptions } = useGetList<NamedItem>(
        'fuels',
        { filter: { chainId }, pagination: { page: 1, perPage: 1000 }, sort: { field: 'name', order: 'ASC' } },
        { enabled: Boolean(chainId), staleTime: FUELS_STALE_MS, retry: false },
    );
    const usage = useDiscountUsage(records, chainId);

    // O título que o cartão do Padrão mostra ("Padrão de Posto Centro") e o nome de um posto viram filtro (tipo e posto):
    // o backend só procura o código e o nome da promoção, e o Padrão não tem nome.
    useEffect(() => {
        const next = typed.trim();
        if (next === appliedSearch.current) return;
        const timer = setTimeout(() => {
            const routed = routeSearch(next, lookups.stations);
            if (routed) {
                appliedSearch.current = '';
                setTyped('');
                updateFilters({
                    search: undefined,
                    ...(routed.onlyDefault ? { type: DiscountTypeKey.default } : {}),
                    ...(routed.placeId ? { placeIds: [routed.placeId] } : {}),
                });
                return;
            }
            appliedSearch.current = next;
            updateFilters({ search: next || undefined });
        }, 400);
        return () => clearTimeout(timer);
    }, [typed, updateFilters, lookups.stations]);

    // ─── Duplicar e desativar ───────────────────────────────────────────────────
    const { duplicate, busyId } = useDuplicateDiscount();
    const [dupTarget, setDupTarget] = useState<any>(null);
    const dupOpener = useRef<HTMLElement | null>(null);
    const [disableTarget, setDisableTarget] = useState<any>(null);
    const disableOpener = useRef<HTMLElement | null>(null);
    const headingRef = useRef<HTMLHeadingElement>(null);
    // Depois de desativar o cartão sai da aba: o foco vai para o título da página, não para o botão que sumiu.
    const focusHeadingAfterDisable = useRef(false);
    const [toast, setToast] = useState('');
    useEffect(() => {
        if (!toast) return;
        const timer = setTimeout(() => setToast(''), TOAST_MS);
        return () => clearTimeout(timer);
    }, [toast]);

    // ─── Textos de estado ───────────────────────────────────────────────────────
    const filtered = Boolean(search || stationId || fuelId || type !== ALL);
    const countText = total === undefined ? '' : total === 1 ? __t('1 desconto') : __t('%{n} descontos', { n: fmtN(total) });

    let content;
    if (error) {
        content = (
            <Alert severity="error" action={<Button color="inherit" size="small" sx={retrySx} onClick={() => refetch()}>Tentar de novo</Button>}>
                Não foi possível carregar os descontos.
            </Alert>
        );
    } else if (isPending) {
        content = <SkeletonCards />;
    } else if (records.length === 0) {
        content = (
            <Box sx={emptyBoxSx}>
                <Box component="span" sx={{ fontSize: 16, fontWeight: 700, color: INK }}>
                    {filtered ? __t('Nada encontrado com esses filtros') : emptyTitleFor(status)}
                </Box>
                <Box component="span" sx={{ fontSize: 14, color: MUTED }}>
                    {filtered ? __t('Mude a busca, o tipo, o posto ou o combustível para ver mais descontos.') : __t('Quando houver, ele aparece aqui.')}
                </Box>
            </Box>
        );
    } else {
        content = (
            <Box
                component="ul"
                aria-busy={isFetching}
                sx={{ ...cardListSx, opacity: isFetching ? 0.6 : 1 }}
            >
                {records.map(record => (
                    <li key={record.id}>
                        <DiscountCard
                            record={record}
                            lookups={lookups}
                            usage={usage.get(String(record.id))}
                            busy={busyId === record.id}
                            onDuplicate={item => { void duplicate(String(item.id)); }}
                            onDuplicateDates={(item, opener) => { dupOpener.current = opener; setDupTarget(item); }}
                            onDisable={(item, opener) => { disableOpener.current = opener; setDisableTarget(item); }}
                        />
                    </li>
                ))}
            </Box>
        );
    }

    return (
        <Box sx={pageSx}>
            <Title title="Descontos" />

            <Box sx={headerSx}>
                <Box sx={titleBoxSx}>
                    <Typography component="h1" ref={headingRef} tabIndex={-1} sx={{ fontSize: 22, fontWeight: 700, color: INK, outline: 'none' }}>
                        Descontos
                    </Typography>
                    <Typography sx={{ fontSize: 14.5, color: MUTED }}>Preços do app e promoções dos seus postos.</Typography>
                </Box>
                <Box sx={headerActionsSx}>
                    <Box sx={manualSx}>
                        <FeatureIntro
                            id="manual-descontos"
                            title="Como funcionam os descontos"
                            description="Veja o que cada tipo de desconto faz, o que o cliente enxerga no app e como duplicar ou desativar."
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                        >
                            <ManualLinkButton slug="como-criar-desconto" label="Como funciona" />
                        </FeatureIntro>
                    </Box>
                    <ButtonLink to="/discounts/create">Novo desconto</ButtonLink>
                </Box>
            </Box>

            {chainIsEmpty ? (
                <EmptyChain />
            ) : (
                <>
                    <StatusTabs items={tabs} active={status} onPick={key => updateFilters({ status: key === ALL ? undefined : key })} />

                    <Box sx={filtersRowSx}>
                        <SearchField value={typed} onChange={setTyped} />
                        {lookups.stations.length > 1 && (
                            <FilterSelect
                                label={__t('Posto')}
                                allLabel={__t('Todos os postos')}
                                items={lookups.stations}
                                value={stationId}
                                onChange={id => updateFilters({ placeIds: id ? [id] : undefined })}
                            />
                        )}
                        {Boolean((fuelOptions?.length ?? 0) > 1 || fuelId) && (
                            <FilterSelect
                                label={__t('Combustível')}
                                allLabel={__t('Todos os combustíveis')}
                                items={fuelOptions || []}
                                value={fuelId}
                                onChange={id => updateFilters({ fuelIds: id ? [id] : undefined })}
                            />
                        )}
                        <TypeChips items={typeItems} active={type} onPick={key => updateFilters({ type: key === ALL ? undefined : key })} />
                    </Box>

                    <Box component="span" aria-live="polite" sx={countSx}>{countText}</Box>

                    {content}

                    {error ? null : (
                        <Box sx={paginationSx}>
                            <Pagination rowsPerPageOptions={[10, 25, 50]} />
                        </Box>
                    )}
                </>
            )}

            <DuplicateDatesDialog
                record={dupTarget}
                name={dupTarget ? cardTitle(dupTarget, lookups.stations) : ''}
                busy={Boolean(dupTarget) && busyId === dupTarget.id}
                onClose={() => setDupTarget(null)}
                onConfirm={range => { void duplicate(String(dupTarget.id), { startAt: range.startAt, endAt: range.endAt }); }}
                onExited={() => restoreFocus(dupOpener.current)}
            />
            <DisableDiscountDialog
                record={disableTarget}
                name={disableTarget ? cardTitle(disableTarget, lookups.stations) : ''}
                onClose={() => setDisableTarget(null)}
                onDisabled={message => { focusHeadingAfterDisable.current = true; setDisableTarget(null); setToast(message); }}
                onExited={() => {
                    const toHeading = focusHeadingAfterDisable.current;
                    focusHeadingAfterDisable.current = false;
                    restoreFocus(toHeading ? headingRef.current : disableOpener.current);
                }}
            />
            <ListToast message={toast} onClose={() => setToast('')} />
        </Box>
    );
};

const DiscountList = () => {
    const chainId = getChainId();
    // Filtro fixo: vale por cima de qualquer `chainId` que sobrou no endereço (rede trocada com a página aberta).
    const chainFilter = useMemo(() => (chainId ? { chainId } : undefined), [chainId]);
    return (
        <Permission permission={PERMISSIONS.FUEL_DISCOUNTS}>
            <ListBase
                resource="discounts"
                perPage={PER_PAGE}
                sort={SORT}
                filter={chainFilter}
                filterDefaultValues={DEFAULT_FILTER}
                // GET /discounts exige a rede (filter.chainId): sem ela o backend devolve 400 "Informe a rede"
                queryOptions={discountListQueryOptions}
            >
                <WaitingChain>
                    <DiscountListPage />
                </WaitingChain>
            </ListBase>
        </Permission>
    );
};

export default DiscountList;
